import { useState, useEffect } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { useAuth, hasUserAuth } from "@/lib/auth";
import { FullPageModal } from "@/components/layout/full-page-modal";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BillingSection } from "@/components/billing-section";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  KINDS,
  KIND_DETAILS,
  type NotificationChannel,
  type NotificationKind,
} from "@/components/notifications/notification-preferences-dialog";

const sections = [
  { id: "billing", label: "Plan & billing" },
  { id: "profile", label: "Profile" },
  { id: "notifications", label: "Notifications" },
];

function ProfileSection() {
  const { auth } = useAuth();
  const utils = trpc.useUtils();
  const { data: profile, isLoading } = trpc.profile.me.useQuery(undefined, {
    enabled: hasUserAuth(auth),
  });

  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [initialized, setInitialized] = useState(false);

  // Initialize form state from loaded profile
  if (profile && !initialized) {
    setDisplayName(profile.displayName ?? "");
    setAvatarUrl(profile.avatarUrl ?? "");
    setInitialized(true);
  }

  const updateMutation = trpc.profile.update.useMutation({
    onSuccess: () => {
      toast.success("Profile updated");
      utils.profile.me.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to update profile"),
  });

  function handleSave() {
    const updates: { displayName?: string; avatarUrl?: string } = {};
    const trimmedName = displayName.trim();
    const trimmedAvatar = avatarUrl.trim();

    if (trimmedName && trimmedName !== (profile?.displayName ?? "")) {
      updates.displayName = trimmedName;
    }
    if (trimmedAvatar !== (profile?.avatarUrl ?? "")) {
      updates.avatarUrl = trimmedAvatar || undefined;
    }

    if (Object.keys(updates).length === 0) {
      toast.info("No changes to save");
      return;
    }

    updateMutation.mutate(updates);
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-5 w-40 animate-pulse rounded bg-muted" />
        <div className="h-9 w-full animate-pulse rounded bg-muted" />
        <div className="h-5 w-40 animate-pulse rounded bg-muted" />
        <div className="h-9 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">Profile</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Your public identity across the workspace.
        </p>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              value={auth?.email ?? ""}
              disabled
              className="bg-muted/50"
            />
            <p className="text-xs text-muted-foreground">
              Email cannot be changed here. Contact support to update your
              email.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">Display name</Label>
            <Input
              id="displayName"
              placeholder="Jane Doe"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="avatarUrl">Avatar URL</Label>
            <div className="flex items-center gap-3">
              <Input
                id="avatarUrl"
                placeholder="https://example.com/avatar.jpg"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                className="flex-1"
              />
              {avatarUrl.trim() && (
                <img
                  src={avatarUrl.trim()}
                  alt="Avatar preview"
                  className="h-9 w-9 shrink-0 rounded-full object-cover border"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
            </div>
          </div>

          <div className="pt-2">
            <Button onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function NotificationPreferencesSection() {
  const { auth } = useAuth();
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.notification.preferences.useQuery(
    undefined,
    {
      enabled: hasUserAuth(auth),
    },
  );

  const [channels, setChannels] = useState<
    Record<NotificationKind, NotificationChannel>
  >({
    "finding-assigned": "in-app",
    mentioned: "in-app",
    regressed: "in-app",
    "tracker-conflict": "in-app",
  });
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (data?.preferences && !initialized) {
      const next = { ...channels };
      for (const p of data.preferences) {
        next[p.kind as NotificationKind] = p.channel as NotificationChannel;
      }
      setChannels(next);
      setInitialized(true);
    }
  }, [data, initialized]);

  const updateMutation = trpc.notification.updatePreferences.useMutation({
    onSuccess: () => {
      toast.success("Notification preferences saved");
      utils.notification.preferences.invalidate();
    },
    onError: (err) => {
      toastTrpcError(err, "Failed to save preferences");
    },
  });

  const handleSave = () => {
    const preferences = KINDS.map((kind) => ({
      kind,
      channel: channels[kind],
    }));
    updateMutation.mutate({ preferences });
  };

  if (isLoading && !initialized) {
    return (
      <div className="space-y-3">
        <div className="h-5 w-40 animate-pulse rounded bg-muted" />
        <div className="h-20 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">
          Notification preferences
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure how you receive updates for assigned findings, mentions,
          regressions and tracker conflicts.
        </p>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-3">
            {KINDS.map((kind) => {
              const detail = KIND_DETAILS[kind];
              const current = channels[kind];

              return (
                <div
                  key={kind}
                  className="flex items-center justify-between gap-4 rounded-lg border border-border/60 p-3 bg-muted/20"
                >
                  <div className="space-y-0.5 min-w-0 pr-2">
                    <Label className="text-sm font-medium leading-none text-foreground">
                      {detail.label}
                    </Label>
                    <p className="text-xs text-muted-foreground leading-normal mt-1">
                      {detail.description}
                    </p>
                  </div>
                  <div className="shrink-0 w-[160px]">
                    <Select
                      value={current}
                      onValueChange={(val: NotificationChannel) =>
                        setChannels((prev) => ({ ...prev, [kind]: val }))
                      }
                      disabled={isLoading || updateMutation.isPending}
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in-app">In-app only</SelectItem>
                        <SelectItem value="in-app+email">
                          In-app + Email
                        </SelectItem>
                        <SelectItem value="off">Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pt-2">
            <Button
              onClick={handleSave}
              disabled={isLoading || updateMutation.isPending}
            >
              {updateMutation.isPending ? "Saving..." : "Save preferences"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

const WEB_APP_URL =
  (import.meta.env.VITE_WEB_APP_URL as string | undefined) ||
  "https://iamu.dev";

export function AccountPage() {
  const { auth } = useAuth();
  const location = useLocation();
  const hash = location.hash?.replace("#", "");
  const [activeSection, setActiveSection] = useState(
    hash?.replace("section-", "") || "billing",
  );

  // Scroll to section from hash on mount
  useEffect(() => {
    if (!hash) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(hash);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(timer);
  }, [hash]);

  // Track scroll position and update active section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id.replace("section-", ""));
          }
        }
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 },
    );

    for (const section of sections) {
      const el = document.getElementById(`section-${section.id}`);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  if (auth && !hasUserAuth(auth)) {
    return <Navigate to="/settings" replace />;
  }

  return (
    <FullPageModal
      title="Account"
      subtitle="Manage your profile, security, and billing"
      onClose={() => window.history.back()}
      stickyHeader={true}
    >
      {/* Floating nav sidebar */}
      <nav className="fixed left-8 top-1/2 -translate-y-1/2 z-10 hidden lg:block">
        <ul className="space-y-3">
          {sections.map((section) => (
            <li key={section.id}>
              <button
                onClick={() => {
                  const el = document.getElementById(`section-${section.id}`);
                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                  setActiveSection(section.id);
                }}
                className={cn(
                  "text-sm transition-colors text-left relative pl-4",
                  activeSection === section.id
                    ? "text-foreground font-medium before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1.5 before:h-1.5 before:rounded-full before:bg-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {section.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-4 sm:px-8 pb-20 pt-4">
        <div className="space-y-12">
          <div id="section-billing" className="space-y-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Plans and limits apply to your active workspace.</span>
              <Link
                to="/settings?tab=billing"
                className="text-primary hover:underline font-medium"
              >
                Open Workspace Settings &rarr;
              </Link>
            </div>
            <BillingSection />
          </div>
          <div id="section-profile">
            <ProfileSection />
          </div>
          <div id="section-notifications">
            <NotificationPreferencesSection />
          </div>
          <div className="flex gap-3 pt-4 text-xs text-muted-foreground">
            <a
              href={`${WEB_APP_URL}/privacy`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground"
            >
              Privacy Policy
            </a>
            <span aria-hidden="true">&middot;</span>
            <a
              href={`${WEB_APP_URL}/terms`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </FullPageModal>
  );
}
